import { useEffect, useState } from 'react'
import { Monitor, Moon, Sun } from 'lucide-react'

import {
  type PreferenciaTema,
  aplicarTema,
  gravarPreferencia,
  lerPreferencia,
  resolverTema,
  sistemaPrefereClaro,
} from '@core/tema'

const OPCOES: { valor: PreferenciaTema; label: string; Icone: typeof Sun }[] = [
  { valor: 'escuro', label: 'Escuro', Icone: Moon },
  { valor: 'claro', label: 'Claro', Icone: Sun },
  { valor: 'sistema', label: 'Como o sistema', Icone: Monitor },
]

/** Escolher o tema da interface.
 *
 *  Não se confunde com o fundo da cena 3D, que vive em Aparência e é
 *  outra coisa: pode-se querer a interface clara com a cena escura, ou
 *  ao contrário. O título do botão diz isso, para ninguém procurar
 *  aqui o que está lá. */
export default function TemaToggle() {
  const [preferencia, setPreferencia] = useState<PreferenciaTema>(lerPreferencia)

  // Seguir o sistema quando é isso que está escolhido. Sem isto, quem
  // escolhe "como o sistema" e muda o tema do computador a meio da
  // tarde fica com a aplicação na cor errada até recarregar.
  useEffect(() => {
    if (preferencia !== 'sistema') return
    if (typeof window.matchMedia !== 'function') return
    const mq = window.matchMedia('(prefers-color-scheme: light)')
    const aoMudar = () => aplicarTema(resolverTema('sistema', mq.matches))
    mq.addEventListener('change', aoMudar)
    return () => mq.removeEventListener('change', aoMudar)
  }, [preferencia])

  const escolher = (p: PreferenciaTema) => {
    setPreferencia(p)
    gravarPreferencia(p)
    aplicarTema(resolverTema(p, sistemaPrefereClaro()))
  }

  const seguinte = (): PreferenciaTema => {
    const i = OPCOES.findIndex((o) => o.valor === preferencia)
    return OPCOES[(i + 1) % OPCOES.length].valor
  }

  const actual = OPCOES.find((o) => o.valor === preferencia) ?? OPCOES[0]
  const Icone = actual.Icone

  return (
    <button
      type="button"
      onClick={() => escolher(seguinte())}
      title={`Tema da interface: ${actual.label}. Clica para trocar. Não é o mesmo que o fundo da cena 3D, que está em Aparência.`}
      aria-label={`Tema da interface: ${actual.label}`}
      className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
    >
      <Icone size={14} aria-hidden />
    </button>
  )
}
